<template>
    <table class="table is-hoverable is-bordered is-fullwidth kata-results-table">
        <thead>
            <tr>
                <th class="has-text-centered kata-place-col">#</th>
                <th>{{ translate("common.competitor") }}</th>
                <th>{{ translate("common.academy") }}</th>
                <th class="has-text-centered">{{ translate("admin_results.kata_judge_scores") }}</th>
                <th class="has-text-centered kata-total-col">{{ translate("scoreboard.kata_total_score") }}</th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="competitor in podiumCompetitors" :key="competitor.id">
                <td class="has-text-centered">{{ competitor.place }}</td>
                <td class="clarity-mask">{{ competitor.firstName }} {{ competitor.lastName }}</td>
                <td>{{ competitor.academy }} / {{ competitor.branch }}</td>
                <td class="has-text-centered">
                    <template v-if="competitor.performances.length > 0">
                        <div v-for="perf in competitor.performances" :key="perf.performanceNumber">
                            <span v-if="competitor.performances.length > 1" class="is-size-7 has-text-grey-light mr-1"
                                >P{{ perf.performanceNumber }}</span
                            >
                            <span
                                v-for="(score, si) in getSortedScores(perf.judgeScores)"
                                :key="si"
                                class="kata-judge-score"
                                :class="{
                                    'kata-judge-score--dropped': isDropped(getSortedScores(perf.judgeScores), si, perf.discardMinMax),
                                }"
                            >
                                {{ score.toFixed(1) }}
                            </span>
                        </div>
                    </template>
                    <span v-else class="has-text-grey">—</span>
                </td>
                <td class="has-text-centered kata-total-score">
                    {{ getTotalScore(competitor) }}
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import { translate } from "../../Common/translations";
    import { KataCategoryPodium, KataPerformance } from "../../Api/competitorResultsApi";
    import { CompetitorWithPlace } from "../../Common/Competitors/types";
    import { getSortedScores, isDropped } from "../../Common/Kata/useKataScores";

    const props = defineProps<{
        categoryPodium: KataCategoryPodium;
    }>();

    type KataPodiumCompetitor = CompetitorWithPlace & {
        performances: KataPerformance[];
    };

    const podiumCompetitors = computed<KataPodiumCompetitor[]>(() => {
        return Object.keys(props.categoryPodium.competitors)
            .map(placeNumber => {
                const competitor = props.categoryPodium.competitors[placeNumber];
                const details = props.categoryPodium.details[competitor.id];
                const performances = details?.performances ?? [];
                return { ...competitor, place: parseInt(placeNumber, 10), performances };
            })
            .sort((a, b) => a.place - b.place);
    });

    function getTotalScore(competitor: KataPodiumCompetitor): string {
        const total = props.categoryPodium.details[competitor.id]?.totalScore;
        if (total === undefined) {
            return "—";
        }
        return total.toFixed(1);
    }
</script>

<style scoped>
    .kata-place-col {
        width: 4rem;
    }

    .kata-total-col {
        width: 7rem;
    }

    .kata-total-score {
        font-weight: bold;
    }
</style>
